import Icon from '@components/atoms/icon';
import { useSolverProfiles } from '@hooks/transfers/useResolvedPreferences';
import { useAITransfersStore } from '@lib/transfers/store/useAITransfersStore';
import { resolveActiveProfileLabel } from '@lib/transfers/utils/profile-label';

type AIProfileChipProps = {
  onClick: () => void;
};

/**
 * Entry point for the AI profile panel: states the active profile beside the
 * Recommended / Custom tab control instead of hiding it behind a bare cog.
 * Renders nothing until the backend profiles have loaded, so it can never
 * open an empty panel.
 */
export const AIProfileChip = ({ onClick }: AIProfileChipProps) => {
  const { data } = useSolverProfiles();
  const selectedProfileId = useAITransfersStore(
    (state) => state.selectedProfileId
  );

  const profiles = data?.data ?? [];
  if (profiles.length === 0) return null;

  const profileLabel = resolveActiveProfileLabel(selectedProfileId, profiles);

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-[44px] flex-shrink-0 items-center gap-[7px] rounded-lg border border-neutral-200 bg-white px-2.5 transition-colors duration-150 hover:bg-neutral-100 md:min-h-0 md:gap-2.5 md:py-[7px] md:pl-3.5 md:pr-3"
      data-cy="ai-profile-chip"
    >
      <span className="text-neutral-600 md:hidden" aria-hidden>
        <Icon name="Settings" fontSize="18px" />
      </span>
      <span className="flex flex-col items-start text-left">
        <span className="hidden font-open-sans text-[10px] font-bold uppercase leading-[1.4] tracking-[0.14em] text-neutral-500 md:block">
          AI profile
        </span>
        <span className="font-open-sans text-[13px] font-bold leading-[1.3] text-neutral-800 md:text-sm">
          {profileLabel}
        </span>
      </span>
      <span
        className="hidden h-7 w-7 items-center justify-center rounded-lg bg-neutral-100 text-neutral-600 md:flex"
        aria-hidden
      >
        <Icon name="Settings" fontSize="18px" />
      </span>
    </button>
  );
};
